import { SystemIntegrationsReadmodel } from '@bett3r-dev/teselly-domain';
/**
 * Logistics State
 *
 * Mirrors the Invoicing subdomain 1:1, adapted to the `logistics` connector
 * kind. A provisioned logistics integration is a `SystemIntegration` (the same
 * canonical readmodel shape used by channels/erps/invoicing), so we reuse
 * `SystemIntegrationsReadmodel` rather than inventing a parallel record type.
 *
 * Credential form state is intentionally NOT in CDSE — it lives locally in
 * `AddLogisticsModal` (React `useState`), per the established convention.
 */

export type LogisticsIntegration = SystemIntegrationsReadmodel;

export interface LogisticsState {
  /** Provisioned logistics integrations, keyed by integration id */
  data: {
    integrations: Record<string, LogisticsIntegration>;
  };

  /** UI state — add modal + selected connector */
  ui: {
    addModalOpen: boolean;
    selectedConnectorId: string | null;
  };

  /** Loading states */
  loading: {
    listLoading: boolean;
    submitting: boolean;
  };

  /**
   * Transient in-flight submission payload. Populated by `submit/start` so the
   * submit policy (which only receives state in `onStateChange`) can read the
   * connector + credential form values to provision. Cleared on
   * success/error.
   */
  pendingSubmission: {
    connectorId: string;
    credentials: Record<string, string | undefined>;
    /**
     * Display name for a `directProvision` instance, entered in the modal and
     * forwarded by the submit policy as the integration `label` so multiple
     * instances of the same connector are distinguishable in the list.
     */
    label?: string;
  } | null;

  /**
   * Transient in-flight deletion marker. Populated by `delete/start` so the
   * delete policy can identify which integration to remove and distinguish a
   * deletion from a submission (both share `loading.submitting`). Cleared on
   * success/error.
   */
  pendingDeletion: {
    integrationId: string;
  } | null;
}

export const initialState: LogisticsState = {
  data: {
    integrations: {}
  },

  ui: {
    addModalOpen: false,
    selectedConnectorId: null
  },

  loading: {
    // Starts true so the list view shows its LoadingOverlay until the
    // list-load policy delivers the first `list/set` snapshot (which clears it).
    listLoading: true,
    submitting: false
  },

  pendingSubmission: null,
  pendingDeletion: null
};

export const selectors = {
  getLogisticsIntegration: ( state: LogisticsState, integrationId: string ) =>
    state.data.integrations[integrationId],

  selectLogisticsIntegrations: ( state: LogisticsState ) =>
    Object.values( state.data.integrations ),

  selectAddModalOpen: ( state: LogisticsState ) =>
    state.ui.addModalOpen,

  selectedConnectorId: ( state: LogisticsState ) =>
    state.ui.selectedConnectorId,

  isListLoading: ( state: LogisticsState ) =>
    state.loading.listLoading,

  isSubmitting: ( state: LogisticsState ) =>
    state.loading.submitting
};
